import type { OrganizationBillingOverviewDto } from '../api/billingPayments'
import type { CategoryBandDto, DealStatsDto, PriceMoveDto } from '../api/marketAnalytics'
import type { ChatMessage } from '../api/chat'
import type { ChatPartner } from '../api/partners'
import type { ProductTypeDto } from '../api/productTypes'
import type {
  RestaurantCatalogFilterOptionsDto,
  RestaurantCatalogOfferDto,
  RestaurantCatalogSearchPageDto,
} from '../api/restaurantCatalog'
import type { MySuppliersPageDto } from '../api/mySuppliers'
import type { RfqUpdatesPageDto } from '../api/rfqUpdates'
import type {
  CatalogItemsPageDto,
  CatalogSummaryDto,
  SupplierCatalogFilterOptionsDto,
  SupplierCatalogItemDto,
} from '../api/supplierCatalog'
import type { TelegramChatSubscriptionDto } from '../api/telegramSubscriptions'
import type { AuthSession, MeResponse, RfqDto, RfqLineDto } from '../api/types'
import type { CartLine } from '../cart/types'

const iso = (daysAgo: number) => {
  const date = new Date()
  date.setDate(date.getDate() - daysAgo)
  return date.toISOString()
}

export const demoProductTypes: ProductTypeDto[] = [
  { id: 1, parentId: null, code: 'meat', name: 'Мясо', active: true },
  { id: 15, parentId: null, code: 'poultry', name: 'Птица', active: true },
  { id: 29, parentId: null, code: 'eggs', name: 'Яйцо', active: true },
  { id: 32, parentId: null, code: 'vegetables', name: 'Овощи', active: true },
  { id: 60, parentId: null, code: 'fruits', name: 'Фрукты', active: true },
  { id: 72, parentId: null, code: 'dairy', name: 'Молочные продукты', active: true },
  { id: 85, parentId: null, code: 'fish', name: 'Рыба', active: true },
  { id: 95, parentId: null, code: 'seafood', name: 'Морепродукты', active: true },
  { id: 105, parentId: null, code: 'groceries', name: 'Бакалея', active: true },
  { id: 2, parentId: 1, code: 'meat.beef', name: 'Говядина', active: true },
  { id: 17, parentId: 15, code: 'poultry.chicken.fillet', name: 'Филе куриное', active: true },
  { id: 34, parentId: 32, code: 'vegetables.root.potato', name: 'Картофель', active: true },
  { id: 40, parentId: 32, code: 'vegetables.leafy.herbs_bunch', name: 'Зелень', active: true },
]

export const demoProductTypesL0Options = (): Array<{ id: number; name: string }> =>
  demoProductTypes
    .filter((type) => type.parentId == null && type.active)
    .map(({ id, name }) => ({ id, name }))

const demoOffers: RestaurantCatalogOfferDto[] = [
  {
    itemId: 101,
    supplierOrganizationId: 20,
    supplierOrganizationName: 'ООО Свежий Склад',
    privateSupplierId: null,
    legalName: 'ООО Свежий Склад',
    inn: '5900000001',
    isRegisteredSupplier: true,
    itemName: 'Филе куриное',
    brand: 'Петелинка',
    country: 'Россия',
    unit: 'kg',
    price: '289.00',
    minShipmentQty: null,
    packageSize: null,
    productTypeId: 17,
    productTypeCode: 'poultry.chicken.fillet',
    productTypeName: 'Птица',
    productTypeActive: true,
    supplierResponseScore: '0.92',
    rankingPrice: '289.00',
    matchedTags: [],
  },
  {
    itemId: 102,
    supplierOrganizationId: 20,
    supplierOrganizationName: 'ООО Свежий Склад',
    privateSupplierId: null,
    legalName: 'ООО Свежий Склад',
    inn: '5900000001',
    isRegisteredSupplier: true,
    itemName: 'Говядина вырезка',
    brand: 'Miratorg',
    country: 'Россия',
    unit: 'kg',
    price: '890.50',
    minShipmentQty: null,
    packageSize: null,
    productTypeId: 2,
    productTypeCode: 'meat.beef',
    productTypeName: 'Говядина',
    productTypeActive: true,
    supplierResponseScore: '0.88',
    rankingPrice: '890.50',
    matchedTags: [],
  },
  {
    itemId: 103,
    supplierOrganizationId: 21,
    supplierOrganizationName: 'ИП Фермеров',
    privateSupplierId: null,
    legalName: 'ИП Фермеров',
    inn: '5900000002',
    isRegisteredSupplier: true,
    itemName: 'Картофель мытый',
    brand: null,
    country: 'Россия',
    unit: 'kg',
    price: '42.00',
    minShipmentQty: null,
    packageSize: null,
    productTypeId: 34,
    productTypeCode: 'vegetables.root.potato',
    productTypeName: 'Картофель',
    productTypeActive: true,
    supplierResponseScore: '0.95',
    rankingPrice: '42.00',
    matchedTags: [],
  },
  {
    itemId: 104,
    supplierOrganizationId: 21,
    supplierOrganizationName: 'ИП Фермеров',
    privateSupplierId: null,
    legalName: 'ИП Фермеров',
    inn: '5900000002',
    isRegisteredSupplier: true,
    itemName: 'Масло сливочное 82,5%',
    brand: 'Вологодское',
    country: 'Россия',
    unit: 'kg',
    price: '620.00',
    minShipmentQty: null,
    packageSize: '5 кг',
    productTypeId: 105,
    productTypeCode: 'grocery',
    productTypeName: 'Бакалея',
    productTypeActive: true,
    supplierResponseScore: null,
    rankingPrice: '620.00',
    matchedTags: [],
  },
  {
    itemId: 105,
    supplierOrganizationId: 22,
    supplierOrganizationName: 'ООО Рыбный Двор',
    privateSupplierId: null,
    legalName: 'ООО Рыбный Двор',
    inn: '5900000003',
    isRegisteredSupplier: true,
    itemName: 'Лосось филе',
    brand: 'Nordic',
    country: 'Норвегия',
    unit: 'kg',
    price: '1450.00',
    minShipmentQty: '2',
    packageSize: null,
    productTypeId: 1,
    productTypeCode: 'meat',
    productTypeName: 'Мясо',
    productTypeActive: true,
    supplierResponseScore: '0.81',
    rankingPrice: '1450.00',
    matchedTags: [],
  },
  {
    itemId: 106,
    supplierOrganizationId: 22,
    supplierOrganizationName: 'ООО Рыбный Двор',
    privateSupplierId: null,
    legalName: 'ООО Рыбный Двор',
    inn: '5900000003',
    isRegisteredSupplier: true,
    itemName: 'Руккола',
    brand: null,
    country: 'Россия',
    unit: 'kg',
    price: '380.00',
    minShipmentQty: null,
    packageSize: null,
    productTypeId: 40,
    productTypeCode: 'greens',
    productTypeName: 'Зелень',
    productTypeActive: true,
    supplierResponseScore: null,
    rankingPrice: '380.00',
    matchedTags: [],
  },
]

export const demoCatalogSearchPage = (): RestaurantCatalogSearchPageDto => ({
  items: demoOffers,
  page: 0,
  size: 50,
  totalElements: demoOffers.length,
  totalPages: 1,
  interpretation: {
    ordering: 'price_asc',
    suppliersCount: 3,
    matchedItemsCount: demoOffers.length,
    tagMatchedItemsCount: 0,
    cheapestPrice: '42.00',
    highestPrice: '1450.00',
    note: 'Демо-выдача: данные для предпросмотра интерфейса.',
  },
})

export const demoCatalogFilterOptions = (): RestaurantCatalogFilterOptionsDto => ({
  brands: ['Petelinka', 'Miratorg', 'Вологодское', 'Nordic'],
  countries: ['Россия', 'Беларусь', 'Норвегия'],
})

const demoRfqLine = (overrides: Partial<RfqLineDto> = {}): RfqLineDto => ({
  id: 1,
  productTypeId: 17,
  quantity: 12,
  unit: 'kg',
  note: null,
  sortOrder: 0,
  supplierSku: 'DEMO-001',
  productName: 'Филе куриное',
  lineStatus: 'active',
  ...overrides,
})

export const demoRfqs: RfqDto[] = [
  {
    id: 501,
    organizationId: 10,
    supplierOrganizationId: 20,
    title: 'Поставка на пятницу',
    displayNumber: '1-130726-1/1',
    batchNumber: 'B-1307',
    partIndex: 1,
    description: 'Срочная поставка на кухню',
    status: 'open',
    createdAt: iso(2),
    updatedAt: iso(0),
    supplierContact: {
      supplierOrganizationId: 20,
      supplierName: 'ООО Свежий Склад',
      contactFullName: 'Алексей Поставкин',
      phone: '+7 912 000-11-22',
      messenger: null,
      email: 'supply@fresh.local',
    },
    lines: [demoRfqLine()],
    desiredDeliveryDate: '2026-07-15',
    desiredDeliveryTimeUntil: '12:00',
    deliveryAddress: 'ул. Ленина, 12',
    supplierDecisionStatus: 'pending',
    rejectionReasonCode: null,
    substitutionComment: null,
    restaurantSubstitutionComment: null,
    customerDisputeComment: null,
  },
  {
    id: 502,
    organizationId: 10,
    supplierOrganizationId: 21,
    title: 'Овощи на выходные',
    displayNumber: '1-120726-2/1',
    batchNumber: null,
    partIndex: null,
    description: null,
    status: 'open',
    createdAt: iso(4),
    updatedAt: iso(1),
    supplierContact: {
      supplierOrganizationId: 21,
      supplierName: 'ИП Фермеров',
      contactFullName: 'Мария Фермерова',
      phone: '+7 912 333-44-55',
      messenger: 'Telegram',
      email: null,
    },
    lines: [
      demoRfqLine({
        id: 2,
        productTypeId: 34,
        productName: 'Картофель мытый',
        quantity: 40,
        unit: 'kg',
      }),
    ],
    desiredDeliveryDate: '2026-07-14',
    desiredDeliveryTimeUntil: '10:00',
    deliveryAddress: 'ул. Ленина, 12',
    supplierDecisionStatus: 'substitution_proposed',
    rejectionReasonCode: null,
    substitutionComment: 'Картофель «Ред Скарлет» вместо мытых — цена ниже на 8%',
    restaurantSubstitutionComment: null,
    customerDisputeComment: null,
  },
  {
    id: 503,
    organizationId: 10,
    supplierOrganizationId: 22,
    title: 'Рыба и зелень',
    displayNumber: '1-110726-1/1',
    batchNumber: null,
    partIndex: null,
    description: 'Для банкета',
    status: 'closed',
    createdAt: iso(9),
    updatedAt: iso(7),
    supplierContact: {
      supplierOrganizationId: 22,
      supplierName: 'ООО Рыбный Двор',
      contactFullName: 'Игорь Рыбаков',
      phone: '+7 912 777-88-99',
      messenger: null,
      email: 'fish@demo.local',
    },
    lines: [
      demoRfqLine({
        id: 3,
        productTypeId: 40,
        productName: 'Руккола',
        quantity: 3,
        unit: 'kg',
      }),
    ],
    desiredDeliveryDate: '2026-07-10',
    desiredDeliveryTimeUntil: '09:00',
    deliveryAddress: 'ул. Ленина, 12',
    supplierDecisionStatus: 'completed',
    rejectionReasonCode: null,
    substitutionComment: null,
    restaurantSubstitutionComment: null,
    customerDisputeComment: null,
  },
  {
    id: 504,
    organizationId: 10,
    supplierOrganizationId: 20,
    title: 'Бакалея на неделю',
    displayNumber: '1-080726-3/1',
    batchNumber: null,
    partIndex: null,
    description: null,
    status: 'open',
    createdAt: iso(5),
    updatedAt: iso(3),
    supplierContact: {
      supplierOrganizationId: 20,
      supplierName: 'ООО Свежий Склад',
      contactFullName: 'Алексей Поставкин',
      phone: '+7 912 000-11-22',
      messenger: null,
      email: 'supply@fresh.local',
    },
    lines: [
      demoRfqLine({
        id: 4,
        productTypeId: 105,
        productName: 'Масло сливочное',
        quantity: 10,
        unit: 'kg',
      }),
    ],
    desiredDeliveryDate: null,
    desiredDeliveryTimeUntil: null,
    deliveryAddress: null,
    supplierDecisionStatus: 'awaiting_customer_confirmation',
    rejectionReasonCode: null,
    substitutionComment: null,
    restaurantSubstitutionComment: null,
    customerDisputeComment: null,
  },
]

export const demoRfqUpdatesPage = (): RfqUpdatesPageDto => ({
  items: [
    {
      id: 901,
      rfqId: 502,
      supplierOrganizationId: 21,
      supplierOrganizationName: 'ИП Фермеров',
      eventType: 'substitution_proposed',
      message: 'Поставщик предложил замену по заявке «Овощи на выходные»',
      read: false,
      createdAt: iso(0),
    },
    {
      id: 902,
      rfqId: 501,
      supplierOrganizationId: 20,
      supplierOrganizationName: 'ООО Свежий Склад',
      eventType: 'accepted',
      message: 'Заявка «Поставка на пятницу» принята в работу',
      read: false,
      createdAt: iso(1),
    },
    {
      id: 903,
      rfqId: 504,
      supplierOrganizationId: 20,
      supplierOrganizationName: 'ООО Свежий Склад',
      eventType: 'awaiting_confirmation',
      message: 'По заявке «Бакалея на неделю» нужно подтвердить выполнение',
      read: true,
      createdAt: iso(2),
    },
    {
      id: 904,
      rfqId: 503,
      supplierOrganizationId: 22,
      supplierOrganizationName: 'ООО Рыбный Двор',
      eventType: 'completed',
      message: 'Заявка «Рыба и зелень» выполнена',
      read: true,
      createdAt: iso(7),
    },
  ],
  page: 0,
  size: 50,
  totalElements: 4,
  totalPages: 1,
})

export const demoSuppliersPage = (): MySuppliersPageDto => ({
  items: [
    {
      supplierOrganizationId: 20,
      supplierName: 'ООО Свежий Склад',
      contactFullName: 'Алексей Поставкин',
      phone: '+7 912 000-11-22',
      messenger: 'Telegram',
      email: 'supply@fresh.local',
      ordersCount: 18,
      lastRfqRequestId: 501,
      lastOrderedAt: iso(0),
    },
    {
      supplierOrganizationId: 21,
      supplierName: 'ИП Фермеров',
      contactFullName: 'Мария Фермерова',
      phone: '+7 912 333-44-55',
      messenger: null,
      email: 'farm@demo.local',
      ordersCount: 11,
      lastRfqRequestId: 502,
      lastOrderedAt: iso(1),
    },
    {
      supplierOrganizationId: 22,
      supplierName: 'ООО Рыбный Двор',
      contactFullName: 'Игорь Рыбаков',
      phone: '+7 912 777-88-99',
      messenger: 'WhatsApp',
      email: 'fish@demo.local',
      ordersCount: 6,
      lastRfqRequestId: 503,
      lastOrderedAt: iso(7),
    },
    {
      supplierOrganizationId: 23,
      supplierName: 'ООО Молочная Линия',
      contactFullName: 'Ольга Молокова',
      phone: '+7 912 555-66-77',
      messenger: null,
      email: 'milk@demo.local',
      ordersCount: 4,
      lastRfqRequestId: null,
      lastOrderedAt: iso(14),
    },
  ],
  page: 0,
  size: 50,
  totalElements: 4,
  totalPages: 1,
})

const demoMyPriceItem = (overrides: Partial<SupplierCatalogItemDto> = {}): SupplierCatalogItemDto => ({
  id: 1001,
  organizationId: 10,
  supplierName: 'Филе куриное охлаждённое',
  matchStatus: 'manual',
  externalSku: 'CHK-01',
  brand: 'Петелинка',
  country: 'Россия',
  unit: 'kg',
  price: 279,
  minShipmentQty: null,
  packageSize: null,
  productTypeId: 17,
  productTypeCode: 'poultry',
  productTypeName: 'Птица',
  productTypeActive: true,
  matchConfidence: null,
  operatorComment: null,
  updatedAt: iso(3),
  freshnessStatus: 'fresh',
  daysSinceUpdate: 3,
  buyerSearchVisible: true,
  privateSupplierId: 501,
  privateSupplierName: 'ИП Демо Поставщик',
  // Поля аналитики цен добавили в DTO вместе с my-prices UX, а в фабрику демо-данных
  // не занесли — из-за этого tsc падал, а значит и сборка market целиком.
  previousPrice: null,
  priceDeltaPct: null,
  previousObservedAt: null,
  ...overrides,
})

export const demoMyPricesSummary = (): CatalogSummaryDto => ({
  totalItems: 8,
  matchAuto: 3,
  matchSuggested: 2,
  matchManual: 2,
  matchUnmatched: 1,
  needsAttention: 2,
  staleItems: 1,
  hiddenFromSearch: 0,
})

export const demoMyPricesItemsPage = (options?: {
  page?: number
  size?: number
  matchStatus?: string
  productTypeId?: number | null
  productTypeIds?: number[]
  unassignedProductType?: boolean
  search?: string
  privateSupplierId?: number | null
}): CatalogItemsPageDto => {
  const allItems = [
    demoMyPriceItem({ id: 1001 }),
    demoMyPriceItem({
      id: 1002,
      supplierName: 'Говядина вырезка премиум',
      brand: 'Miratorg',
      price: 920,
      productTypeId: 2,
      productTypeName: 'Говядина',
      matchStatus: 'auto',
      freshnessStatus: 'stale_7',
      daysSinceUpdate: 9,
    }),
    demoMyPriceItem({
      id: 1003,
      supplierName: 'Картофель мытый',
      brand: null,
      price: 39,
      productTypeId: 34,
      productTypeName: 'Картофель',
      matchStatus: 'suggested',
      matchConfidence: 0.86,
    }),
    demoMyPriceItem({
      id: 1004,
      supplierName: 'Масло сливочное 82,5%',
      brand: 'Вологодское',
      price: 615,
      productTypeId: 105,
      productTypeName: 'Бакалея',
      matchStatus: 'manual',
    }),
    demoMyPriceItem({
      id: 1005,
      supplierName: 'Руккола мытая',
      brand: null,
      price: 360,
      productTypeId: 40,
      productTypeName: 'Зелень',
      matchStatus: 'auto',
    }),
    demoMyPriceItem({
      id: 1006,
      supplierName: 'Лосось филе',
      brand: 'Nordic',
      price: 1420,
      country: 'Норвегия',
      productTypeId: 1,
      productTypeName: 'Мясо',
      matchStatus: 'suggested',
      freshnessStatus: 'stale_14',
      daysSinceUpdate: 16,
    }),
    demoMyPriceItem({
      id: 1007,
      supplierName: 'Сметана 20%',
      brand: 'Простоквашино',
      price: 118,
      productTypeId: null,
      productTypeName: null,
      matchStatus: 'unmatched',
    }),
    demoMyPriceItem({
      id: 1008,
      supplierName: 'Булочка для бургера',
      brand: 'BakeHouse',
      price: 22,
      unit: 'шт',
      productTypeId: 105,
      productTypeName: 'Бакалея',
      matchStatus: 'manual',
      updatedAt: iso(1),
    }),
  ]

  const matchStatus = options?.matchStatus?.trim().toLowerCase() || ''
  const search = options?.search?.trim().toLowerCase() || ''
  const typeIds = new Set(
    (options?.productTypeIds?.length
      ? options.productTypeIds
      : options?.productTypeId != null
        ? [options.productTypeId]
        : []
    ).filter((id) => Number.isFinite(id)),
  )
  const unassignedOnly = Boolean(options?.unassignedProductType)
  const privateSupplierId = options?.privateSupplierId ?? null

  const filtered = allItems.filter((item) => {
    if (privateSupplierId != null && item.privateSupplierId !== privateSupplierId) {
      return false
    }
    if (matchStatus && item.matchStatus.toLowerCase() !== matchStatus) {
      return false
    }
    if (unassignedOnly) {
      if (item.productTypeId != null) {
        return false
      }
    } else if (typeIds.size > 0) {
      if (item.productTypeId == null || !typeIds.has(item.productTypeId)) {
        return false
      }
    }
    if (search) {
      const haystack = [item.supplierName, item.brand, item.externalSku]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      if (!haystack.includes(search)) {
        return false
      }
    }
    return true
  })

  const size = options?.size ?? 50
  const page = options?.page ?? 0
  const start = page * size
  const items = filtered.slice(start, start + size)
  const totalElements = filtered.length
  const totalPages = Math.max(1, Math.ceil(totalElements / size))

  return {
    items,
    page,
    size,
    totalElements,
    totalPages,
  }
}

export const demoMyPricesFilterOptions = (): SupplierCatalogFilterOptionsDto => ({
  brands: ['Петелинка', 'Miratorg', 'Вологодское', 'Nordic', 'BakeHouse'],
  countries: ['Россия', 'Норвегия'],
})

export const demoChatPartners: ChatPartner[] = [
  { organizationId: 20, name: 'ООО Свежий Склад' },
  { organizationId: 21, name: 'ИП Фермеров' },
  { organizationId: 22, name: 'ООО Рыбный Двор' },
]

export const demoChatMessages = (counterpartyOrgId: number, selfOrgId = 10): ChatMessage[] => {
  const base: ChatMessage[] = [
    {
      id: 1,
      threadId: counterpartyOrgId,
      senderUserProfileId: 1,
      senderOrgId: counterpartyOrgId,
      body: 'Добрый день! Подтверждаем поставку на пятницу до 12:00.',
      createdAt: iso(1),
    },
    {
      id: 2,
      threadId: counterpartyOrgId,
      senderUserProfileId: 2,
      senderOrgId: selfOrgId,
      body: 'Отлично, ждём. Можно добавить 5 кг рукколы?',
      createdAt: iso(1),
    },
    {
      id: 3,
      threadId: counterpartyOrgId,
      senderUserProfileId: 1,
      senderOrgId: counterpartyOrgId,
      body: 'Да, добавим в ту же машину без доплаты за доставку.',
      createdAt: iso(0),
    },
  ]
  if (counterpartyOrgId === 21) {
    return [
      {
        id: 11,
        threadId: 21,
        senderUserProfileId: 3,
        senderOrgId: 21,
        body: 'По картофелю предлагаем замену — «Ред Скарлет», качество стабильнее.',
        createdAt: iso(0),
      },
      ...base.slice(1),
    ]
  }
  return base
}

export const demoBillingOverview = (): OrganizationBillingOverviewDto => ({
  catalog: [
    {
      plan: 'start',
      display_name: 'Старт',
      monthly_rub: 0,
      yearly_rub: null,
      description: 'Мои прайсы и бланк заказа',
      seat_limit: 1,
      base_venue_limit: 1,
      devices_per_user_limit: 1,
      max_private_suppliers: 5,
      private_supplier_refresh_days: 7,
      market_catalog_enabled: false,
      platform_rfq_enabled: false,
      platform_chat_enabled: false,
      buyer_suppliers_enabled: false,
      market_price_intelligence_enabled: false,
    },
    {
      plan: 'standard',
      display_name: 'Стандарт',
      monthly_rub: 5_990,
      yearly_rub: 59_900,
      description: 'Каталог, заявки, чат',
      seat_limit: 3,
      base_venue_limit: 1,
      devices_per_user_limit: 1,
      max_private_suppliers: null,
      private_supplier_refresh_days: null,
      market_catalog_enabled: true,
      platform_rfq_enabled: true,
      platform_chat_enabled: true,
      buyer_suppliers_enabled: true,
      market_price_intelligence_enabled: false,
    },
    {
      plan: 'premium',
      display_name: 'Премиум',
      monthly_rub: 7_990,
      yearly_rub: 79_900,
      description: 'Мульти-точка, расширенные лимиты и аналитика цен',
      seat_limit: 6,
      base_venue_limit: 3,
      max_employees_per_venue: 2,
      devices_per_user_limit: null,
      max_private_suppliers: null,
      private_supplier_refresh_days: null,
      market_catalog_enabled: true,
      platform_rfq_enabled: true,
      platform_chat_enabled: true,
      buyer_suppliers_enabled: true,
      market_price_intelligence_enabled: true,
    },
  ],
  addon_venue_monthly_rub: 3_000,
  addon_venue_yearly_rub: 30_000,
  premium_base_venues: 2,
  current: {
    plan: 'standard',
    plan_display_name: 'Стандарт',
    subscription_status: 'active',
    subscription_status_display_name: 'Активна',
    billing_interval: 'monthly',
    period_end: '2026-08-13',
    paid_venue_slots: 0,
    seats_used: 2,
    seats_limit: 3,
    venues_used: 1,
    venues_limit: 1,
  },
  premium_quote: {
    venue_count: 3,
    monthly_rub: 10_990,
    yearly_rub: 109_900,
    addon_venue_slots: 1,
  },
})

export const demoTelegramSubscriptions = (): TelegramChatSubscriptionDto[] => [
  {
    id: 1,
    botType: 'market',
    purpose: 'order_status',
    chatId: '123456789',
    organizationId: 10,
    active: true,
    lastSeenAt: iso(0),
  },
]

export const demoCartLines: CartLine[] = [
  {
    catalogItemId: 101,
    supplierKind: 'registered',
    supplierOrganizationId: 20,
    privateSupplierId: null,
    supplierOrganizationName: 'ООО Свежий Склад',
    productTypeId: 17,
    productTypeName: 'Птица',
    itemName: 'Филе куриное',
    brand: 'Петелинка',
    unit: 'kg',
    price: '289.00',
    quantity: 12,
    note: '',
  },
  {
    catalogItemId: 103,
    supplierKind: 'registered',
    supplierOrganizationId: 21,
    privateSupplierId: null,
    supplierOrganizationName: 'ИП Фермеров',
        productTypeId: 34,
    productTypeName: 'Картофель',
    itemName: 'Картофель мытый',
    brand: null,
    unit: 'kg',
    price: '42.00',
    quantity: 30,
    note: 'Средний калибр',
  },
  {
    catalogItemId: 104,
    supplierKind: 'registered',
    supplierOrganizationId: 21,
    privateSupplierId: null,
    supplierOrganizationName: 'ИП Фермеров',
    productTypeId: 105,
    productTypeName: 'Бакалея',
    itemName: 'Масло сливочное 82,5%',
    brand: 'Вологодское',
    unit: 'kg',
    price: '620.00',
    quantity: 5,
    note: '',
  },
  {
    catalogItemId: 999,
    supplierKind: 'external',
    supplierOrganizationId: null,
    privateSupplierId: 501,
    supplierOrganizationName: 'Прайс «Утренний рынок»',
    productTypeId: 40,
    productTypeName: 'Зелень',
    itemName: 'Базилик зелёный',
    brand: null,
    unit: 'kg',
    price: '450.00',
    quantity: 2,
    note: 'Из внешнего прайса',
  },
]

export const demoBuyerMe = (): MeResponse => ({
  id: 1001,
  email: 'demo.buyer@provisium.local',
  displayName: 'Демо Покупатель',
  first_name: 'Демо',
  last_name: 'Покупатель',
  phone: '+7 900 000-00-00',
  roles: ['BUYER'],
  organizations: [
    {
      id: 10,
      org_type: 'buyer',
      legal_name: 'ООО Демо Ресторан',
      trade_name: 'Демо Ресторан',
      member_role: 'owner',
      plan: 'premium',
      plan_display_name: 'Премиум',
      market_catalog_enabled: true,
      platform_rfq_enabled: true,
      platform_chat_enabled: true,
      buyer_suppliers_enabled: true,
      market_price_intelligence_enabled: true,
      venues_limit: 2,
      venues_used: 1,
      paid_venue_slots: 0,
      venues: [
        {
          id: 1,
          name: 'Основная точка',
          sort_order: 0,
          active: true,
        },
      ],
    },
  ],
})

export const demoBuyerSession = (): AuthSession => ({
  accessToken: 'demo-access-token',
  refreshToken: 'demo-refresh-token',
  organizationId: 10,
  email: 'demo.buyer@provisium.local',
  displayName: 'Демо Покупатель',
})

export const demoDealStats = (): DealStatsDto => ({
  deal_count: 18,
  total_amount: 412_450.5,
  estimated_savings: 38_420,
  by_supplier: [
    { key_id: 20, deal_count: 8, total_amount: 198_200, estimated_savings: 18_200 },
    { key_id: 21, deal_count: 6, total_amount: 124_800.5, estimated_savings: 11_100 },
    { key_id: 22, deal_count: 3, total_amount: 62_450, estimated_savings: 6_120 },
    { key_id: 23, deal_count: 1, total_amount: 27_000, estimated_savings: 3_000 },
  ],
  by_category: [
    { key_id: 17, deal_count: 7, total_amount: 156_300, estimated_savings: 14_800 },
    { key_id: 34, deal_count: 5, total_amount: 98_400, estimated_savings: 9_200 },
    { key_id: 2, deal_count: 4, total_amount: 89_750.5, estimated_savings: 8_420 },
    { key_id: 40, deal_count: 2, total_amount: 68_000, estimated_savings: 6_000 },
  ],
})

export const demoPriceMoves = (): PriceMoveDto[] => [
  {
    catalog_item_id: 101,
    organization_id: 20,
    product_type_id: 17,
    unit: 'кг',
    previous_price: 312,
    current_price: 289,
    delta_abs: -23,
    delta_pct: -7.4,
    observed_at: iso(0),
    is_private: true,
    private_supplier_id: 1,
    supplier_name: 'АгроСнаб',
    item_name: 'Филе куриное охл.',
  },
  {
    catalog_item_id: 204,
    organization_id: 21,
    product_type_id: 34,
    unit: 'кг',
    previous_price: 42,
    current_price: 48.5,
    delta_abs: 6.5,
    delta_pct: 15.5,
    observed_at: iso(1),
    is_private: true,
    private_supplier_id: 2,
    supplier_name: 'ОвощТорг',
    item_name: 'Картофель мытый',
  },
  {
    catalog_item_id: 310,
    organization_id: 22,
    product_type_id: 2,
    unit: 'кг',
    previous_price: 890,
    current_price: 845,
    delta_abs: -45,
    delta_pct: -5.1,
    observed_at: iso(1),
    is_private: true,
    private_supplier_id: 1,
    supplier_name: 'АгроСнаб',
    item_name: 'Лосось с/м',
  },
  {
    catalog_item_id: 118,
    organization_id: 20,
    product_type_id: 17,
    unit: 'кг',
    previous_price: 265,
    current_price: 278,
    delta_abs: 13,
    delta_pct: 4.9,
    observed_at: iso(2),
    is_private: true,
    private_supplier_id: 2,
    supplier_name: 'ОвощТорг',
    item_name: 'Филе лосося с/м',
  },
  {
    catalog_item_id: 401,
    organization_id: 21,
    product_type_id: 40,
    unit: 'пучок',
    previous_price: 95,
    current_price: 79,
    delta_abs: -16,
    delta_pct: -16.8,
    observed_at: iso(3),
    is_private: true,
    private_supplier_id: 1,
    supplier_name: 'АгроСнаб',
    item_name: 'Укроп пучок',
  },
]

export const demoCategoryBands = (productTypeId: number): CategoryBandDto[] => {
  const base =
    productTypeId === 34 ? 44 : productTypeId === 2 ? 860 : productTypeId === 40 ? 88 : 290
  return Array.from({ length: 28 }, (_, index) => {
    const daysAgo = 27 - index
    const wobble = Math.sin(index / 3.5) * (base * 0.05)
    const avg = base + wobble
    const date = new Date()
    date.setUTCDate(date.getUTCDate() - daysAgo)
    return {
      band_date: date.toISOString().slice(0, 10),
      product_type_id: productTypeId,
      source_kind: 'list',
      unit: 'кг',
      min_price: Number((avg * 0.88).toFixed(2)),
      max_price: Number((avg * 1.14).toFixed(2)),
      avg_price: Number(avg.toFixed(2)),
      median_price: Number((avg * 0.98).toFixed(2)),
      sample_count: 8 + (index % 5),
    }
  })
}
